import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import React from 'react'
import { useSortableData } from '@/modules/drivers/hooks/useSortTable'
import { getGroupedViolationsColumnsNew } from '@/modules/drivers/constants/tables'
import TableEmpty from '@/modules/drivers/shared/tableItems/TableEmpty'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { ViolationRowItem } from './ViolationRowItem'
import useViolationService from '@/modules/drivers/services/ViolationService'
import { useDate } from '@/modules/drivers/stores/dateStore'
import { GroupedViolationModel } from '@/app/models/groupedViolationModel'
import { addDays } from 'date-fns'

const ViolationsGrupedTable = ({ drivers, companyId }) => {
  const { t } = useLocalize()
  const columns = getGroupedViolationsColumnsNew(t)
  const { range } = useDate()
  const { getGroupedViolations } = useViolationService()

  const [violations, setViolations] = React.useState<GroupedViolationModel[]>(
    [],
  )
  const [isLoading, setIsLoading] = React.useState(false)

  React.useEffect(() => {
    const fetchViolations = async () => {
      if (!range?.startDate || !range?.endDate || !companyId) return

      const driverIdsToFetch = drivers.map((d) => d.driverId)

      if (driverIdsToFetch.length === 0) {
        setViolations([])
        return
      }

      setIsLoading(true)
      try {
        const startDate = new Date(
          range.startDate.year!,
          range.startDate.month! - 1,
          range.startDate.day!,
        )
        const endDate = new Date(
          range.endDate.year!,
          range.endDate.month! - 1,
          range.endDate.day!,
        )

        const result = await getGroupedViolations({
          companyId,
          drivers: driverIdsToFetch,
          startTime: startDate,
          endTime: addDays(endDate, 1),
        })
        setViolations(result || [])
      } catch (error) {
        console.error('Error fetching grouped violations:', error)
        setViolations([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchViolations()
  }, [range, drivers, companyId])

  const { sortedData, handleHeaderClick, sortConfig } = useSortableData({
    data: violations,
    tableColumns: columns,
  })

  return (
    <section className="tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden tw-rounded-t-xl">
      {isLoading ? (
        <div className="tw-grid tw-h-full tw-place-items-center">
          <LoaderIcon className="tw-w-16 tw-animate-spin" />
        </div>
      ) : violations.length > 0 ? (
        <div className="tw-flex tw-flex-1 tw-flex-col tw-overflow-y-auto">
          <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
            <TableHeader
              opacity
              sticky
              rounded
              columns={columns}
              sortConfig={sortConfig}
              disabledCheckboxToggle
              onHeaderClick={handleHeaderClick}
            />

            <tbody>
              {sortedData.map((violation, i) => (
                <ViolationRowItem
                  key={`${violation?.driverId ?? 'unknown'}-${i}`}
                  violation={violation}
                  companyId={companyId}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <TableHeader
            opacity
            disabledCheckboxToggle
            columns={columns}
            sortConfig={null}
          />
          <TableEmpty title={t('driverModule.no-drivers')} />
        </>
      )}
    </section>
  )
}

export default ViolationsGrupedTable
